import styled from "styled-components";
import { isSameDay } from "date-fns";
import { useEffect, useState } from "react";
import { DatePickerStyles } from "../../../types";
import { useTranslation } from "react-i18next";

const CalendarContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  text-align: center;
`;

const CalendarDayHeader = styled.div<{
  styles: DatePickerStyles;
}>`
  font-weight: 700;
  color: ${({ styles }) => styles.CalendarMonthDayTextColour};
  padding: 6px;
`;

// Converts hex to rgba in order to set the opacity to 30%.
const hexToRgba = (hex: string) => {
  hex = hex.replace(/^#/, "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, 30%)`;
};

const CalendarDay = styled.div<{
  isDatePast?: boolean;
  selected?: boolean;
  styles: DatePickerStyles;
}>`
  color: ${({ isDatePast, selected, styles }) => {
    if (isDatePast) return hexToRgba(styles.CalendarDayTextColour);
    return selected
      ? styles.CalendarSelectedTextColour
      : styles.CalendarDayTextColour;
  }};
  background-color: ${({ isDatePast, selected, styles }) => {
    if (isDatePast) return hexToRgba(styles.CalendarDayBackgroundColour);
    return selected
      ? styles.CalendarSelectedBackgroundColour
      : styles.CalendarDayBackgroundColour;
  }};
  border-radius: ${({ styles }) => styles.CalendarDayBorderRadius}px;
  font-weight: ${({ isDatePast }) => (isDatePast ? "400" : "500")};
  text-decoration: ${({ isDatePast }) =>
    isDatePast ? "line-through" : "none"};
  padding: 6px;

  &:hover {
    ${({ isDatePast, selected }) => {
      if (!isDatePast && !selected) return "background:#f0f0f0";
    }};
    cursor: ${({ isDatePast }) => (isDatePast ? "default" : "pointer")};
  }

  &:focus {
    outline: 2px solid black;
  }
`;

export type CalendarProps = {
  month: Date;
  setMonth: React.Dispatch<React.SetStateAction<Date>>;
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  styles: DatePickerStyles;
};

export const Calendar: React.FC<CalendarProps> = ({
  month,
  setMonth,
  selectedDate,
  setSelectedDate,
  styles,
}) => {
  const { t } = useTranslation();

  const [date, setDate] = useState(new Date(month));

  useEffect(() => {
    setDate(new Date(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    setDate(new Date(month));
  }, [month]);

  const numberOfDaysInMonth = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0
  ).getDate();

  // Day of the week
  const firstDayOfMonth = new Date(
    date.getFullYear(),
    date.getMonth(),
    1
  ).getDay();

  const days = Array.from(Array(numberOfDaysInMonth), (_, index) => index + 1);

  const numberOfDaysInPrevMonth = new Date(
    date.getFullYear(),
    date.getMonth(),
    0
  ).getDate();

  const daysFromPrevMonth: Array<Date> = new Array(firstDayOfMonth)
    .fill(null)
    .map((_, index) => {
      const prevMonthDate = new Date(
        date.getFullYear(),
        date.getMonth() - 1,
        numberOfDaysInPrevMonth - index
      );
      return prevMonthDate;
    })
    .reverse();

  const totalDays = 42; // 6 rows * 7 days

  const numberOfExtraDaysNeeded =
    totalDays - (numberOfDaysInMonth + firstDayOfMonth);

  const daysFromNextMonth: Array<Date> = new Array(numberOfExtraDaysNeeded)
    .fill(null)
    .map((_, index) => {
      return new Date(date.getFullYear(), date.getMonth() + 1, index + 1);
    });

  const isDatePast = (selectedDate: Date) => {
    return selectedDate < new Date(new Date().setHours(0, 0, 0, 0));
  };

  const handleDateClick = (day: Date) => {
    setSelectedDate(day);
    setMonth(day);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    day: Date
  ) => {
    const currentDayElement = event.target as HTMLElement;
    const currentIndex = parseInt(currentDayElement.dataset.index ?? "0");

    if (event.key === "ArrowRight") {
      const nextElement = document.querySelector(
        `[data-index="${currentIndex + 1}"]`
      ) as HTMLElement;
      nextElement && nextElement.focus();
    }
    if (event.key === "ArrowLeft") {
      const prevElement = document.querySelector(
        `[data-index="${currentIndex - 1}"]`
      ) as HTMLElement;
      prevElement && prevElement.focus();
    }
    if (event.key === "ArrowDown") {
      const downElement = document.querySelector(
        `[data-index="${currentIndex + 7}"]`
      ) as HTMLElement;
      downElement && downElement.focus();
    }
    if (event.key === "ArrowUp") {
      const upElement = document.querySelector(
        `[data-index="${currentIndex - 7}"]`
      ) as HTMLElement;
      upElement && upElement.focus();
    }
    if (event.key === "Enter") {
      handleDateClick(day);
    }
  };

  const daysOfWeek: string[] = [
    t("Sunday"),
    t("Monday"),
    t("Tuesday"),
    t("Wednesday"),
    t("Thursday"),
    t("Friday"),
    t("Saturday"),
  ];
  const formattedDaysOfWeek = daysOfWeek.map((day) => day.slice(0, 2));

  const getDateDataIdString = (date: Date) => {
    return `${("0" + date.getDate()).slice(-2)}-${("0" + (date.getMonth() + 1)).slice(-2)}-${date.getFullYear()}`;
  };

  return (
    <CalendarContainer data-id="calendar-container">
      {formattedDaysOfWeek.map((day) => (
        <CalendarDayHeader key={day} styles={styles} data-id={day}>
          {day}
        </CalendarDayHeader>
      ))}
      {daysFromPrevMonth.map((prevMonthDate, index) => {
        const formattedDay = prevMonthDate.toLocaleString("default", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        return (
          <CalendarDay
            aria-label={t("SelectItem", { item: formattedDay })}
            key={`prev${index}`}
            data-index={index}
            selected={isSameDay(prevMonthDate, selectedDate)}
            tabIndex={0}
            styles={styles}
            {...(isDatePast(prevMonthDate)
              ? { isDatePast: true }
              : {
                  onClick: () => handleDateClick(prevMonthDate),
                  onKeyDown: (event) => handleKeyDown(event, prevMonthDate),
                })}
            data-id={getDateDataIdString(prevMonthDate)}
          >
            {prevMonthDate.getDate()}
          </CalendarDay>
        );
      })}
      {days.map((day, index) => {
        const currentMonthDay = new Date(
          date.getFullYear(),
          date.getMonth(),
          day
        );
        const formattedDay = currentMonthDay.toLocaleString("default", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        return (
          <CalendarDay
            aria-label={t("SelectItem", { item: formattedDay })}
            key={day}
            data-index={firstDayOfMonth + index}
            selected={isSameDay(currentMonthDay, selectedDate)}
            tabIndex={0}
            styles={styles}
            {...(isDatePast(currentMonthDay)
              ? {
                  isDatePast: true,
                }
              : {
                  onClick: () => handleDateClick(currentMonthDay),
                  onKeyDown: (event) => handleKeyDown(event, currentMonthDay),
                })}
            data-id={`${getDateDataIdString(date)}${isSameDay(currentMonthDay, selectedDate) ? "-selected" : ""}`}
          >
            {day}
          </CalendarDay>
        );
      })}
      {daysFromNextMonth.map((nextMonthDate, index) => {
        const formattedDay = nextMonthDate.toLocaleString("default", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        return (
          <CalendarDay
            aria-label={t("SelectItem", { item: formattedDay })}
            key={`next${index}`}
            data-index={firstDayOfMonth + days.length + index}
            selected={isSameDay(nextMonthDate, selectedDate)}
            tabIndex={0}
            styles={styles}
            {...(isDatePast(nextMonthDate)
              ? { isDatePast: true }
              : {
                  onClick: () => handleDateClick(nextMonthDate),
                  onKeyDown: (event) => handleKeyDown(event, nextMonthDate),
                })}
            data-id={getDateDataIdString(nextMonthDate)}
          >
            {nextMonthDate.getDate()}
          </CalendarDay>
        );
      })}
    </CalendarContainer>
  );
};

export default Calendar;
